export type Word = {
  text: string;
  pace: number;
  intensity: number;
  pauseAfterMs?: number;
  cueStart?: boolean;
};
export type PromptMode = "teleprompter" | "cue-cards";
export type Script = { title: string; words: Word[]; mode?: PromptMode };
export type Card = { start: number; end: number; words: Word[] };

/** Explicit JSON boundaries win; plain text uses blank lines between cards. */
export function scriptCards(words: Word[]): Card[] {
  const cards: Card[] = [];
  const explicit = words.some((word) => word.cueStart);
  words.forEach(function (word, index) {
    const startsCard =
      index === 0 ||
      word.cueStart ||
      (!explicit && /\n\s*\n/.test(words[index - 1].text));
    if (startsCard) cards.push({ start: index, end: index, words: [] });
    const card = cards[cards.length - 1];
    card.end = index;
    card.words.push(word);
  });
  return cards;
}

export function parseCards(text: string): Word[] {
  const cards = text
    .trim()
    .split(/\n\s*\n/)
    .filter((part) => part.trim());
  return cards.flatMap(function (text, index) {
    const words = parseWords(
      text.trim() + (index < cards.length - 1 ? "\n\n" : ""),
    );
    words[0].cueStart = true;
    return words;
  });
}
export type Cue = { start: number; end: number; word: Word; index: number };
export const DEFAULT_PACE = 140;
export const DEMO_TEXT =
  "You don’t need a perfect moment to begin.\n\nYou just need an idea worth sharing.\n\nTake a breath. Find your voice. And make something that feels like you.";

export function parseWords(text: string): Word[] {
  const matches = text.match(/\S+\s*/g) || [];
  return matches.map(function (text) {
    return { text, pace: DEFAULT_PACE, intensity: 1 };
  });
}

// Preserve direction on a range so a second tap can extend it either way.
export function selectionRange(
  words: Word[],
  index: number,
  scope: string,
  anchor: number | null,
): [number, number] {
  if (scope === "all") return [0, words.length - 1];
  if (scope === "range" && anchor !== null)
    return [Math.min(anchor, index), Math.max(anchor, index)];
  if (scope !== "sentence") return [index, index];
  let start = index;
  let end = index;
  while (start > 0 && !/[.!?][”"']?\s*$/.test(words[start - 1].text)) start--;
  while (end < words.length - 1 && !/[.!?][”"']?\s*$/.test(words[end].text))
    end++;
  return [start, end];
}

export function buildTimeline(words: Word[]): Cue[] {
  let elapsed = 0;
  return words.map(function (word, index) {
    const start = elapsed;
    elapsed += 60 / word.pace + (word.pauseAfterMs || 0) / 1000;
    return { start, end: elapsed, word, index };
  });
}

export function cueAt(timeline: Cue[], elapsed: number): number {
  let low = 0;
  let high = timeline.length - 1;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const cue = timeline[middle];
    if (elapsed < cue.start) high = middle - 1;
    else if (elapsed >= cue.end) low = middle + 1;
    else return middle;
  }
  return -1;
}

export function formatTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
}

export function readScript(storage: Pick<Storage, "getItem">): Script {
  try {
    const saved = JSON.parse(storage.getItem("cue-script") || "null");
    const validWords =
      Array.isArray(saved?.words) &&
      saved.words.length <= 10000 &&
      saved.words.every(function (word: Word) {
        return (
          typeof word.text === "string" &&
          Number.isFinite(word.pace) &&
          word.pace >= 60 &&
          word.pace <= 240 &&
          [0, 1, 2].includes(word.intensity) &&
          (word.cueStart === undefined || typeof word.cueStart === "boolean") &&
          (word.pauseAfterMs === undefined ||
            (Number.isInteger(word.pauseAfterMs) &&
              word.pauseAfterMs >= 0 &&
              word.pauseAfterMs <= 10000))
        );
      });
    const validMode =
      saved?.mode === undefined ||
      ["teleprompter", "cue-cards"].includes(saved.mode);
    if (typeof saved?.title === "string" && validWords && validMode)
      return saved;
  } catch {
    /* An unreadable draft must not prevent opening the app. */
  }
  const words = parseWords(DEMO_TEXT);
  words[4].intensity = 2;
  words[5].intensity = 2;
  return { title: "A little courage", words };
}
